// ----- Phase 5.X-submission-reorder — Lifecycle impact panel ----- //
//
// Renders the response from POST
// /api/v1/submissions/reorder/evaluate-lifecycle. Embedded in
// ReorderModal: appears below the draggable list whenever the
// operator's pending order has at least one affected document.
//
// V2 vs V1 difference: V1 surfaced a "Fix All" button that re-targeted
// each broken replace pointer to the new highest-prior sequence
// number. V2's lifecycle target is a document ID (not a sequence
// number) — there is no deterministic auto-fix because the operator
// must pick a specific *document* in the new target sequence.
// Consequence: `autoFixable` is always `false` and the panel shows a
// disabled "Fix All" button with an explanatory tooltip, plus a per-row
// "Open inspector" link.

import { type ReactElement } from 'react';
import type { AffectedDocument } from '@/lib/api/reorder';

export interface LifecycleReportPanelProps {
  readonly affectedDocuments: readonly AffectedDocument[];
  readonly unaffectedDocumentCount: number;
  readonly evaluating?: boolean;
  /** Optional click handler when operator wants to inspect a doc. */
  readonly onOpenDocument?: (documentId: string) => void;
}

export function LifecycleReportPanel({
  affectedDocuments,
  unaffectedDocumentCount,
  evaluating = false,
  onOpenDocument,
}: LifecycleReportPanelProps): ReactElement {
  // Evaluating: subtle banner so operator knows the report is
  // recomputing rather than empty.
  if (evaluating) {
    return (
      <div
        data-testid="lifecycle-report-evaluating"
        style={{
          padding: '12px 14px',
          borderRadius: 6,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          fontSize: 13,
          color: 'var(--text-muted)',
        }}
      >
        Evaluating lifecycle impact…
      </div>
    );
  }

  if (affectedDocuments.length === 0) {
    return (
      <div
        data-testid="lifecycle-report-clean"
        style={{
          padding: '12px 14px',
          borderRadius: 6,
          background: '#ecfdf5',
          border: '1px solid #a7f3d0',
          color: '#065f46',
          fontSize: 13,
        }}
      >
        <strong>No lifecycle conflicts.</strong>{' '}
        {unaffectedDocumentCount === 0
          ? 'No documents reference each other across these sequences.'
          : `${unaffectedDocumentCount} cross-sequence lifecycle reference${
              unaffectedDocumentCount === 1 ? '' : 's'
            } checked — all remain valid under this ordering.`}{' '}
        {/* phase-reorder-lifecycle-guard — the check is now ENFORCED: the same
            evaluation re-runs on the server at apply time and refuses a
            conflict, so this is a gate, not just a note. It is re-checked at
            apply, so a change made after this panel rendered is still caught. */}
        Re-checked and enforced when you apply.
      </div>
    );
  }

  const anyAutoFixable = affectedDocuments.some((d) => d.autoFixable);

  return (
    <div
      data-testid="lifecycle-report-panel"
      style={{
        padding: '12px 14px',
        borderRadius: 6,
        background: '#fef3c7',
        border: '1px solid #fcd34d',
        color: '#78350f',
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <strong>
          {affectedDocuments.length} document
          {affectedDocuments.length === 1 ? '' : 's'} would have a broken lifecycle
          reference under this reorder.
        </strong>
        <button
          type="button"
          disabled={!anyAutoFixable}
          aria-disabled={!anyAutoFixable}
          title={
            anyAutoFixable
              ? 'Fix all auto-fixable rows in one click'
              : 'In V2, lifecycle targets are specific documents (not sequence numbers). Open each row in the Document Inspector to pick a new target or drop the lifecycle operation back to "new".'
          }
          style={{
            padding: '4px 10px',
            borderRadius: 4,
            border: '1px solid #92400e',
            background: anyAutoFixable ? '#f59e0b' : 'var(--surface)',
            color: anyAutoFixable ? '#fff' : 'var(--text-muted)',
            cursor: anyAutoFixable ? 'pointer' : 'not-allowed',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Fix All
        </button>
      </div>
      {/* phase-reorder-lifecycle-guard — the honest, enforcing statement. This
          panel used to only WARN while Apply proceeded regardless; now Apply is
          blocked and the server refuses the same conflict authoritatively. */}
      <p
        data-testid="lifecycle-report-blocked"
        style={{ margin: '6px 0 0 0', fontWeight: 600 }}
      >
        Apply is blocked until these are resolved — a reorder that breaks a
        cross-sequence lifecycle reference cannot be saved.
      </p>
      <ul style={{ margin: '8px 0 0 0', padding: '0 0 0 18px' }}>
        {affectedDocuments.map((d) => (
          <li key={d.documentId} style={{ marginBottom: 6 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {d.documentName}
            </span>{' '}
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              ({d.currentLifecycleOperation})
            </span>
            <div style={{ marginTop: 2, fontSize: 12 }}>{d.impactDescription}</div>
            {onOpenDocument !== undefined && (
              <button
                type="button"
                onClick={() => onOpenDocument(d.documentId)}
                style={{
                  marginTop: 4,
                  padding: '2px 8px',
                  borderRadius: 4,
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  cursor: 'pointer',
                  fontSize: 11,
                }}
              >
                Open inspector
              </button>
            )}
          </li>
        ))}
      </ul>
      {unaffectedDocumentCount > 0 && (
        <p style={{ margin: '8px 0 0 0', color: '#78350f', fontSize: 12 }}>
          {unaffectedDocumentCount} other cross-sequence lifecycle reference
          {unaffectedDocumentCount === 1 ? '' : 's'} remain valid.
        </p>
      )}
    </div>
  );
}
